import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';

import * as api from '../../../../services/trm/trmServices';
import { Checkbox, Collapse, Modal, Space, Spin, notification } from 'antd';
import {
  CaretRightOutlined,
  CloseOutlined,
  SettingOutlined
} from '@ant-design/icons';

import { NO_CONFIG_SET } from '../constants';
import EmptySetComp from './EmptySetComp';
import SelectedSetComp from './SelectedSetComp';
import DynamicProperties from '../../../common/DynamicProperties';
import style from './styles.scss';
import NonEditableSetsComp from './NonEditableSetsComp';

const networkLayerObject = new api.NetworkLayer();

const { confirm } = Modal;
const { Panel } = Collapse;

const CustomModal = styled(Modal)`
  .ant-modal-header {
    padding: 12px;
  }
  .ant-modal-content {
    padding: 12px 12px 0px 12px;
  }

  /* Reduce padding for the modal body content */
  .ant-modal-body {
    padding: 12px;
  }
  h6 {
    padding-top: 12px;
    padding-left: 36px;
  }
`;

const TypologiesAccordion = ({
  typologyData,
  liveSets,
  typologySets,
  setTypologySets,
  scenarioId,
  setLoading,
  executed,
  configureRunInstanceActions,
  dynpropsTabAccess = false
}) => {
  const currentTypologySet = typologySets.find(
    (f) => f.typologyId === typologyData.typology.id
  );

  const [open, setOpen] = useState([]);
  const [showSetComp, setShowSetComp] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const selectedSegmentFromSet =
    currentTypologySet?.segmentCombinationIds.filter((f) =>
      liveSets?.data?.combinations?.some((c) => c.id === f)
    ) || [];

  const selectedSetDifferentFromAllSets =
    currentTypologySet?.segmentCombinationIds.filter((f) =>
      liveSets?.data?.combinations?.every((c) => c.id !== f)
    ) || [];

  const allSelected =
    selectedSegmentFromSet.length === liveSets?.data?.combinations?.length ||
    false;

  const isNotConfigured =
    selectedSegmentFromSet.length === 0 && !currentTypologySet.useDefault;

  const deleteComponent = () => (
    <CloseOutlined
      onClick={(event) => {
        event.stopPropagation();
        deleteTypology();
      }}
    />
  );

  const deleteTypology = () => {
    confirm({
      title: 'Delete',
      content: `Are you sure you want to delete '${currentTypologySet.typologyName}'?`,
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: {
        style: {
          backgroundColor: '#0c8ed7'
        }
      },
      onOk: () => {
        setLoading(true);
        networkLayerObject
          .removeTypologyFromScenarioAPI({
            typologyIds: [currentTypologySet.typologyId],
            scenarioID: scenarioId
          })
          .then((res) => {
            setTypologySets((prev) =>
              prev.filter((p) => p.typologyId !== currentTypologySet.typologyId)
            );
            configureRunInstanceActions.fetchScenarioTypologies({
              id: scenarioId
            });
            notification.open({
              message: (
                <p>
                  <i style={{ color: '#2ecc71' }} className="fa fa-check"></i>
                  &nbsp;&nbsp;Success
                </p>
              ),
              description: `Deleted Successfully.`
            });
          })
          .catch((err) => {
            notification.open({
              message: (
                <p>
                  <i style={{ color: '#e74c3c' }} className="fa fa-close"></i>
                  &nbsp;&nbsp;{err.message || `Something went wrong`}
                </p>
              ),
              description: ''
            });
          })
          .finally(() => setLoading(false));
      }
    });
  };

  const dynPropsModalComponent = () => {
    const {
      typology: { title, id: typologyId }
    } = typologyData;
    const screenWidth = window.innerWidth;
    const modalWidth = `${(85 * screenWidth) / 100}px`;
    return (
      <CustomModal
        id="typology-modal"
        title="Dynamic Properties"
        visible={isModalVisible}
        destroyOnClose
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        width={modalWidth}
        footer={false}>
        <h6>Typology Name: {title}</h6>
        <DynamicProperties
          subModule="typology"
          moduleId="3"
          runInstanceId={scenarioId}
          typologyId={typologyId}
        />
      </CustomModal>
    );
  };

  const handleDefault = (event) => {
    event.stopPropagation();
    setTypologySets((prev) =>
      prev.map((p) => {
        if (p.typologyId === currentTypologySet.typologyId) {
          return {
            ...p,
            useDefault: event.target.checked
          };
        } else {
          return p;
        }
      })
    );
  };

  return (
    <>
      <Collapse
        bordered={false}
        activeKey={open}
        onChange={() => setOpen((prev) => (prev.length > 0 ? [] : [1]))}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
        className={classNames(
          style.siteCollapseCustomCollapse,
          style.accordionContainer
        )}
        style={{
          backgroundColor: 'white',
          paddingBottom: '10px'
        }}>
        <Panel
          header={
            <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>{typologyData.typology.title}</div>
              <div className={style.accordionNoTypoText}>
                {isNotConfigured && !executed && open.length === 0
                  ? NO_CONFIG_SET
                  : ''}
                {executed &&
                typologyData.combinations?.filter((f) => f.id !== -1).length ===
                  0 &&
                !typologyData.useDefault &&
                open.length === 0
                  ? NO_CONFIG_SET
                  : ''}
                {dynpropsTabAccess && (
                  <SettingOutlined
                    style={{ color: '#000', fontSize: 20 }}
                    onClick={() => setIsModalVisible(true)}
                  />
                )}
              </div>
            </Space>
          }
          key="1"
          onChange={() => setOpen((prev) => (prev.length > 0 ? [] : [1]))}
          className={classNames(
            style.siteCollapseCustomPanel,
            style.accordionHeader
          )}
          extra={deleteComponent()}>
          <div>
            {executed ? (
              <>
                <NonEditableSetsComp
                  liveSets={liveSets}
                  typologyData={typologyData}
                />
              </>
            ) : (
              <>
                {selectedSetDifferentFromAllSets.length > 0 && (
                  <div className={style.noSetsAvailable}>
                    Configuration parameters are changed since the run instance
                    configuration so user needs to configuration again.
                  </div>
                )}
                <p className={style.pickHeader}>Pick a set</p>
                {showSetComp ||
                typologyData.combinations?.filter((f) => f.id !== -1).length >
                  0 ? (
                  <SelectedSetComp
                    liveSets={liveSets}
                    allSelected={allSelected}
                    currentTypologySet={currentTypologySet}
                    setTypologySets={setTypologySets}
                  />
                ) : (
                  <EmptySetComp setShowSetComp={setShowSetComp} />
                )}
              </>
            )}
            <div className={style.TomsWrapper}>
              <Checkbox
                disabled={executed}
                className={style.defaultCheckbox}
                checked={currentTypologySet?.useDefault}
                onChange={handleDefault}>
                Apply to all other configuration Set (Default)
              </Checkbox>
              <span>
                {allSelected ? (
                  <p className={style.allSelectTag}>all selected</p>
                ) : (
                  ''
                )}
              </span>
            </div>
          </div>
          {isModalVisible && dynPropsModalComponent()}
        </Panel>
      </Collapse>
    </>
  );
};

TypologiesAccordion.propTypes = {
  liveSets: PropTypes.object,
  typologySets: PropTypes.array,
  typologyData: PropTypes.object,
  setTypologySets: PropTypes.func,
  scenarioId: PropTypes.number,
  setLoading: PropTypes.func,
  dynpropsTabAccess: PropTypes.bool,
  executed: PropTypes.bool,
  configureRunInstanceActions: PropTypes.object
};

export default TypologiesAccordion;
